import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "../../../ui/Button/Button";
import type { GuardianTeenGigRequestResponse } from "../../../../types/guardianTypes";
import { gigRequestStatusLabel } from "../../../../types/types";
import { formatGigCompensation } from "../../../../utils/GigCompensationUtils";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../../../utils/DateUtils";
import { formatCityStateZip } from "../../../../utils/LocationUtils";
import { filterTeenGigRequests, getTeenRequestFilterCounts, type TeenRequestFilter } from "./utils";

type TeenProfileGigRequestsSectionProps = {
  requests: GuardianTeenGigRequestResponse[];
  decidingKey: string | null;
  onDecideRequest: (requestId: string, decision: "APPROVED" | "REJECTED") => Promise<void>;
  onOpenPayoutSetup: () => void;
};

const TeenProfileGigRequestsSection = ({
  requests,
  decidingKey,
  onDecideRequest,
  onOpenPayoutSetup,
}: TeenProfileGigRequestsSectionProps) => {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<TeenRequestFilter>("needsApproval");
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const highlightedRequestId = searchParams.get("requestId");
  const counts = useMemo(() => getTeenRequestFilterCounts(requests), [requests]);
  const visibleRequests = useMemo(() => filterTeenGigRequests(requests, filter), [filter, requests]);

  const filterButtons = [
    { key: "needsApproval" as const, label: "Needs Approval", count: counts.needsApproval },
    { key: "approved" as const, label: "Approved Approvals", count: counts.approved },
    { key: "declined" as const, label: "Declined Approvals", count: counts.declined },
    { key: "all" as const, label: "All Gigs", count: counts.all },
  ];
  const emptyMessage = {
    needsApproval: "No gig requests need your approval right now.",
    approved: "No approved gig requests yet.",
    declined: "No declined gig requests yet.",
    all: "No gig requests yet.",
  }[filter];

  useEffect(() => {
    if (!highlightedRequestId) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilter("all");
    setExpandedRequestId(highlightedRequestId);
  }, [highlightedRequestId]);

  useEffect(() => {
    if (!highlightedRequestId) {
      return;
    }

    const highlightedCard = document.getElementById(`guardian-teen-request-${highlightedRequestId}`);
    if (highlightedCard) {
      highlightedCard.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedRequestId, visibleRequests]);

  return (
    <section className="rounded-[2rem] border border-myteengig-border bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Applied Gigs</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-myteengig-text-primary">Applied gigs and guardian approvals</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-myteengig-text-muted">
            See requests waiting on your decision, requests you approved, and requests you declined. All Gigs includes every outcome.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {filterButtons.map((button) => (
            <button
              key={button.key}
              type="button"
              aria-pressed={filter === button.key}
              onClick={() => setFilter(button.key)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                filter === button.key
                  ? "border-myteengig-primary bg-myteengig-primary text-white"
                  : "border-myteengig-border bg-myteengig-background text-myteengig-text-primary hover:border-myteengig-primary"
              }`}
            >
              {button.label} · {button.count}
            </button>
          ))}
        </div>
      </div>

      {visibleRequests.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background px-5 py-6 text-sm text-myteengig-text-muted">
          {emptyMessage}
        </div>
      ) : (
        <div className="mt-6 grid gap-4">
          {visibleRequests.map((request) => {
            const location = formatCityStateZip(
              {
                locationType: request.gigLocationType,
                city: request.gigCity,
                state: request.gigState,
                zip: request.gigZip,
              },
              "Location not set",
            );
            const detailsAreOpen = expandedRequestId === request.requestId;
            const isHighlighted = request.requestId === highlightedRequestId;

            return (
              <article
                key={request.requestId}
                id={`guardian-teen-request-${request.requestId}`}
                className={`rounded-[1.5rem] border p-5 transition ${
                  isHighlighted
                    ? "border-myteengig-primary bg-[linear-gradient(180deg,_rgba(239,248,255,1),_rgba(255,255,255,1))] ring-2 ring-myteengig-primary/20"
                    : "border-myteengig-border bg-[linear-gradient(180deg,_rgba(255,255,255,1),_rgba(234,248,255,0.72))]"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-lg font-semibold text-myteengig-text-primary">{request.gigTitle ?? "Unknown gig"}</h3>
                          <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-xs font-semibold text-myteengig-text-primary">
                            {gigRequestStatusLabel[request.requestStatus] ?? request.requestStatus}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-myteengig-text-muted">
                          <span>{request.gigCategory ?? "Category not set"}</span>
                          <span>{location}</span>
                          <span>{formatGigCompensation(request.paymentAmountCents, request.volunteer, "Not set")}</span>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-myteengig-text-muted">
                          <span>Requested {formatDateTime(request.createdAt)}</span>
                          <span>Proposed {formatDateTimeInGigTimeZone(request.proposedStartTime, request.gigTimeZone)} to {formatDateTimeInGigTimeZone(request.proposedEndTime, request.gigTimeZone)}</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedRequestId((current) => current === request.requestId ? null : request.requestId)}
                        className="w-full sm:w-auto"
                      >
                        {detailsAreOpen ? "Hide Details" : "View Details"}
                      </Button>
                    </div>

                    {detailsAreOpen ? (
                      <div className="mt-4 grid gap-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Requested</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">{formatDateTime(request.createdAt)}</p>
                          </div>
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Proposed Time</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">
                              {formatDateTimeInGigTimeZone(request.proposedStartTime, request.gigTimeZone)} to {formatDateTimeInGigTimeZone(request.proposedEndTime, request.gigTimeZone)}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-white/80 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Confirmed Time</p>
                            <p className="mt-2 text-sm text-myteengig-text-primary">
                              {formatDateTimeInGigTimeZone(request.confirmedStartTime, request.gigTimeZone)} to {formatDateTimeInGigTimeZone(request.confirmedEndTime, request.gigTimeZone)}
                            </p>
                          </div>
                        </div>

	                        <div className="rounded-2xl bg-white/80 px-4 py-3">
	                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Gig Description</p>
	                          <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">{request.gigDescription ?? "No description provided."}</p>
	                        </div>

	                        {request.gigRequiresEquipment ? (
	                          <div className="rounded-2xl bg-white/80 px-4 py-3">
	                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Equipment or supplies</p>
	                            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
	                              {request.gigEquipmentDescription?.trim() || "Equipment is required, but details were not provided."}
	                            </p>
	                          </div>
	                        ) : null}

	                        <div className="rounded-2xl bg-white/80 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Teen Message</p>
                          <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">{request.requestMessage ?? "No message provided."}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="xl:w-56">
                    {request.requestStatus === "GUARDIAN_PENDING" ? (
                      <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-semibold text-amber-900">Guardian action needed</p>
                        <p className="mt-2 text-sm leading-6 text-amber-800">
                          This request is waiting on your decision before it can move forward to the gig poster.
                        </p>
                        <div className="mt-4 grid gap-3">
                          <Button
                            size="sm"
                            disabled={decidingKey !== null}
                            onClick={() => void onDecideRequest(request.requestId, "APPROVED")}
                          >
                            {decidingKey === `${request.requestId}-APPROVED` ? "Approving..." : "Approve"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={decidingKey !== null}
                            onClick={() => void onDecideRequest(request.requestId, "REJECTED")}
                          >
                            {decidingKey === `${request.requestId}-REJECTED` ? "Declining..." : "Decline"}
                          </Button>
                        </div>
                      </div>
                    ) : request.requestStatus === "GUARDIAN_REJECTED" ? (
                      <div className="rounded-[1.5rem] border border-rose-200 bg-rose-50 p-4">
                        <p className="text-sm font-semibold text-rose-900">Guardian declined</p>
                        <p className="mt-2 text-sm leading-6 text-rose-800">
                          {request.canReverseGuardianRejection
                            ? "Changed your mind? Approve this request to send it to the gig poster for review."
                            : "This gig is no longer open for requests."}
                        </p>
                        {request.canReverseGuardianRejection ? (
                          <Button
                            size="sm"
                            className="mt-4 w-full"
                            disabled={decidingKey !== null}
                            onClick={() => void onDecideRequest(request.requestId, "APPROVED")}
                          >
                            {decidingKey === `${request.requestId}-APPROVED` ? "Approving..." : "Approve instead"}
                          </Button>
                        ) : null}
                      </div>
                    ) : request.requestStatus === "PAYOUT_SETUP_PENDING" ? (
                      <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-semibold text-amber-900">Payout setup needed</p>
                        <p className="mt-2 text-sm leading-6 text-amber-800">
                          The poster selected this teen, but the gig cannot be fully confirmed or funded until guardian-managed Stripe payout setup is complete.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          className="mt-4 w-full"
                          onClick={onOpenPayoutSetup}
                        >
                          Open payout setup
                        </Button>
                      </div>
                    ) : request.requestStatus === "FUNDING_PENDING" ? (
                      <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm font-semibold text-emerald-900">Waiting on poster payment</p>
                        <p className="mt-2 text-sm leading-6 text-emerald-800">
                          Payout setup is ready. The poster still needs to pay to fully confirm this gig before it becomes scheduled.
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background p-4 text-sm text-myteengig-text-muted">
                        <p>No guardian action is needed on this request right now.</p>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TeenProfileGigRequestsSection;
